import { formatoPrecio } from '@/lib/utils/formato-precio';
import type { ClaveDeSalida, RangoDeRegla, SalidaSinPrecio } from '../types/pricing-rules.types';

/**
 * Las salidas de un precio sin precio posible, en palabras (spec 227 §5). Las
 * usan la explicación (`lectura-del-precio.ts`, `PorQueEstePrecio`) y Por decidir
 * (`fila-sin-precio.tsx`, que las importa). Vivían en ese componente: una lib
 * pura no importa un componente (informe 229, código).
 */

/** El nombre de una regla de `rangos`, o «una regla borrada» si ya no está. */
export const nombreDeLaRegla = (rangos: RangoDeRegla[] | null | undefined, id: string): string =>
  rangos?.find((r) => r.ruleId === id)?.ruleName ?? 'una regla borrada';

/**
 * «Mantener tu margen», «Cumplir las otras reglas», «Cumplir «No mover más de
 * 15 %»», «No moverlo» (spec 227 §5). OTRAS nombra la regla si las otras son
 * una sola (las blandas: las que no son el margen; una que no se pudo medir, con
 * `aviso`, no cuenta). `idEnPrueba`: en «¿Y si…?» y Nueva regla, la regla que se
 * prueba se llama «la regla nueva».
 */
export function tituloDeSalida(
  clave: ClaveDeSalida,
  rangos: RangoDeRegla[] | null | undefined,
  idEnPrueba: string | null = null
): string {
  const cumplir = (id: string) =>
    id === idEnPrueba ? 'Cumplir la regla nueva' : `Cumplir «${nombreDeLaRegla(rangos, id)}»`;
  if (clave === 'MARGEN') return 'Mantener tu margen';
  if (clave === 'NO_MOVER') return 'No moverlo';
  if (clave === 'OTRAS') {
    const otras = [
      ...new Set((rangos ?? []).filter((r) => !r.duro && r.aviso == null).map((r) => r.ruleId)),
    ];
    return otras.length === 1 ? cumplir(otras[0]) : 'Cumplir las otras reglas';
  }
  return cumplir(clave.slice('REGLA:'.length));
}

/** El piso del margen: lo que se sirve mientras nadie decide si hoy está por debajo (decisión 3). */
export function pisoDelMargen(rangos: RangoDeRegla[] | null | undefined): number | null {
  const pisos = (rangos ?? [])
    .filter((r) => r.duro && r.tipo === 'MARGEN_MINIMO' && r.aviso == null && r.min != null)
    .map((r) => r.min!);
  return pisos.length ? Math.max(...pisos) : null;
}

/** La explicación de cada salida. La de NO_MOVER con el precio de hoy bajo el margen dice lo que se sirve (decisión 3). */
export function explicacionDeSalida(
  s: SalidaSinPrecio,
  precioHoy: number,
  rangos: RangoDeRegla[] | null | undefined
): string {
  if (s.clave === 'NO_MOVER') {
    const piso = pisoDelMargen(rangos);
    if (piso != null && precioHoy < piso)
      return `El precio publicado queda en ${formatoPrecio(precioHoy)}; mientras tanto se sirve ${formatoPrecio(piso)} para no vender bajo el margen.`;
  }
  return s.explicacion;
}
